/* eslint-disable react/prop-types */
import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
  useLayoutEffect,
  useId,
} from "react";
import { createPortal } from "react-dom";
import { Trash2 } from "lucide-react";
import CustomFormField, { getFieldIds } from "../CustomFormField/CustomFormField";
import "../CustomTheme/customTheme.css";
import "./CustomDropdowns.css";

/**
 * Default label extractor. Handles:
 *  - plain strings / numbers               -> "Delhi"
 *  - { stage }                             -> item.stage
 *  - { collegeId: { collegeName } }        -> item.collegeId.collegeName
 *  - { type } (when type === "type")       -> item.type
 *  - { en: { name } }                      -> item.en.name
 *  - { name }                              -> item.name
 * Pass `getOptionLabel` to override entirely for a different shape.
 */
const defaultGetOptionLabel = (item, optionType) => {
  if (item === null || item === undefined) return "";
  if (typeof item === "string" || typeof item === "number") return String(item);
  if (item.stage) return item.stage;
  if (item.collegeId?.collegeName) return item.collegeId.collegeName;
  if (item.type && optionType === "type") return item.type;
  if (item.en?.name) return item.en.name;
  if (item.name) return item.name;
  if (item.title) return item.title;
  if (item.label) return item.label;
  if (item.examYear) return item.examYear;
  if (typeof item.value === "string" || typeof item.value === "number") return String(item.value);
  if (item._id || item.id) return String(item._id ?? item.id);
  return "";
};

const defaultGetOptionKey = (item, index) => {
  if (item === null || item === undefined) return `idx-${index}`;
  if (typeof item === "string" || typeof item === "number") return `${item}-${index}`;
  return item._id ?? item.id ?? `idx-${index}`;
};

const isSameOption = (a, b) => {
  if (a === b) return true;
  if (a === null || a === undefined || b === null || b === undefined) return false;
  if (typeof a !== "object" || typeof b !== "object") return a === b;
  const keyA = a._id ?? a.id;
  const keyB = b._id ?? b.id;
  if (keyA !== undefined && keyB !== undefined) return keyA === keyB;
  return JSON.stringify(a) === JSON.stringify(b);
};

const isEmptyPlainObject = (v) =>
  typeof v === "object" && v !== null && !Array.isArray(v) && Object.keys(v).length === 0;

function CustomDropdowns({
  label,
  labelName, // kept for backward compatibility with the old prop name
  isRequired,
  isMandatary, // kept for backward compatibility with the old prop name
  colClass,
  className,
  style,
  options = [],
  value,
  onChange,
  setInputText, // kept for backward compatibility with the old prop name
  placeholder,
  disabled,
  type,
  search = true, // whether the menu shows a type-to-filter search box
  clearable = true,
  getOptionLabel,
  getOptionKey,
  error,
  hint,
  id,
  noMargin = true, // dropdowns never had bottom spacing; existing screens rely on that
}) {
  const [searchText, setSearchText] = useState("");
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0); // keyboard highlight in the visible options

  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const searchInputRef = useRef(null);

  const autoId = useId();
  const triggerId = id || `cdd-${autoId}`;
  const fieldIds = getFieldIds(triggerId);
  const listboxId = `${triggerId}-listbox`;
  const optionId = (i) => `${triggerId}-opt-${i}`;

  const fieldLabel = label ?? labelName;
  const required = isRequired ?? isMandatary;
  const handleChange = onChange ?? setInputText ?? (() => {});
  const hasRealHandler = Boolean(onChange || setInputText);
  const emptyLabel = placeholder ?? (fieldLabel ? `Select ${fieldLabel}` : "Select");

  const extractLabel = useCallback(
    (item) => (getOptionLabel ? getOptionLabel(item) : defaultGetOptionLabel(item, type)),
    [getOptionLabel, type]
  );
  const extractKey = useCallback(
    (item, index) => (getOptionKey ? getOptionKey(item, index) : defaultGetOptionKey(item, index)),
    [getOptionKey]
  );

  const hasValue =
    value !== "" && value !== null && value !== undefined && !isEmptyPlainObject(value);
  const selectedLabel = hasValue ? extractLabel(value) : "";

  if (process.env.NODE_ENV !== "production") {
    if (!hasRealHandler) {
      // eslint-disable-next-line no-console
      console.warn(
        `Dropdown${fieldLabel ? ` ("${fieldLabel}")` : ""}: no "onChange" (or "setInputText") ` +
          "prop was passed, so clicking an option does nothing — it's calling a no-op. " +
          "Pass onChange={yourHandler}."
      );
    }
    if (!Array.isArray(options) || options.length === 0) {
      // eslint-disable-next-line no-console
      console.warn(
        `Dropdown${fieldLabel ? ` ("${fieldLabel}")` : ""}: "options" is empty or not an array — ` +
          "there's nothing to select.",
        options
      );
    }
  }

  /* ---------- portal positioning: opens toward whichever side has room ---------- */

  const MENU_MAX_HEIGHT = 240;
  const VIEWPORT_MARGIN = 8;

  const computeMenuPosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const openUpward = spaceBelow < MENU_MAX_HEIGHT + VIEWPORT_MARGIN && spaceAbove > spaceBelow;

    setMenuStyle({
      left: rect.left,
      width: rect.width,
      top: openUpward ? undefined : rect.bottom + 6,
      bottom: openUpward ? window.innerHeight - rect.top + 6 : undefined,
      maxHeight: Math.max(
        120,
        Math.min(MENU_MAX_HEIGHT, (openUpward ? spaceAbove : spaceBelow) - VIEWPORT_MARGIN - 6)
      ),
      placement: openUpward ? "top" : "bottom",
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    computeMenuPosition();
  }, [open, computeMenuPosition]);

  // Reposition on scroll (capture:true also catches scroll inside any
  // scrollable ancestor, not just the window) and resize, so the menu
  // keeps tracking the trigger instead of drifting or getting clipped.
  useEffect(() => {
    if (!open) return;
    const onReposition = () => computeMenuPosition();
    window.addEventListener("scroll", onReposition, true);
    window.addEventListener("resize", onReposition);
    return () => {
      window.removeEventListener("scroll", onReposition, true);
      window.removeEventListener("resize", onReposition);
    };
  }, [open, computeMenuPosition]);

  useEffect(() => {
    const closeDropdown = (e) => {
      if (
        triggerRef.current &&
        !triggerRef.current.contains(e.target) &&
        menuRef.current &&
        !menuRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", closeDropdown);
    return () => document.removeEventListener("mousedown", closeDropdown);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    if (search) searchInputRef.current?.focus();
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, search]);

  const onClear = useCallback(
    (e) => {
      e?.stopPropagation();
      handleChange("");
      setSearchText("");
      setOpen(false);
    },
    [handleChange]
  );

  const filteredOptions = useMemo(() => {
    if (!Array.isArray(options)) return [];
    if (!search || !searchText) return options;
    const needle = searchText.toLowerCase();
    // labels can be numbers (examYear, stage) — String() so search never throws
    return options.filter((item) => String(extractLabel(item) ?? "").toLowerCase().includes(needle));
  }, [options, search, searchText, extractLabel]);

  const handleSelect = (item) => {
    handleChange(item);
    setOpen(false);
    setSearchText("");
    triggerRef.current?.focus();
  };

  /* ---------- keyboard: APG "select-only combobox" pattern ---------- */

  const openMenu = (index) => {
    if (disabled) return;
    const selected = filteredOptions.findIndex((o) => isSameOption(o, value));
    setActiveIndex(index ?? (selected >= 0 ? selected : 0));
    setOpen(true);
  };

  const closeMenu = (refocus) => {
    setOpen(false);
    setSearchText("");
    if (refocus) triggerRef.current?.focus();
  };

  const onKeyDown = (e) => {
    const count = filteredOptions.length;
    const inSearch = e.target === searchInputRef.current;
    if (!open) {
      if (["ArrowDown", "ArrowUp", "Enter", " "].includes(e.key)) {
        e.preventDefault();
        openMenu(e.key === "ArrowUp" ? Math.max(0, count - 1) : undefined);
      }
      return;
    }
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        if (count) setActiveIndex((i) => (i + 1) % count);
        break;
      case "ArrowUp":
        e.preventDefault();
        if (count) setActiveIndex((i) => (i - 1 + count) % count);
        break;
      case "Home":
        if (inSearch) break; // Home/End move the caret while typing
        e.preventDefault();
        setActiveIndex(0);
        break;
      case "End":
        if (inSearch) break;
        e.preventDefault();
        setActiveIndex(Math.max(0, count - 1));
        break;
      case "Enter":
        e.preventDefault(); // never submit the surrounding form
        if (count) handleSelect(filteredOptions[Math.min(activeIndex, count - 1)]);
        break;
      case "Escape":
        e.preventDefault();
        e.stopPropagation(); // only the menu closes, not a drawer/modal around it
        closeMenu(true);
        break;
      case "Tab":
        closeMenu(false);
        break;
      default:
        break;
    }
  };

  // keep the highlighted option in view while arrowing through a long list
  useEffect(() => {
    if (!open) return;
    document.getElementById(optionId(activeIndex))?.scrollIntoView?.({ block: "nearest" });
  }, [open, activeIndex]);

  const activeId = open && filteredOptions.length ? optionId(Math.min(activeIndex, filteredOptions.length - 1)) : undefined;

  const menu =
    open && menuStyle
      ? createPortal(
          <div
            ref={menuRef}
            className={`cdd-menu-portal cdd-menu-${menuStyle.placement}`}
            style={{
              position: "fixed",
              left: menuStyle.left,
              width: menuStyle.width,
              top: menuStyle.top,
              bottom: menuStyle.bottom,
            }}
          >
            {search && (
              <input
                ref={searchInputRef}
                type="text"
                role="searchbox"
                aria-label={`Search ${fieldLabel || "options"}`}
                aria-controls={listboxId}
                aria-activedescendant={activeId}
                className="cdd-searchInput"
                placeholder="Search..."
                value={searchText}
                onChange={(e) => {
                  setSearchText(e.target.value);
                  setActiveIndex(0);
                }}
                onKeyDown={onKeyDown}
              />
            )}
            <div id={listboxId} role="listbox" aria-label={fieldLabel || emptyLabel} className="cdd-optionsList" style={{ maxHeight: menuStyle.maxHeight }}>
              {filteredOptions.length > 0 ? (
                filteredOptions.map((item, index) => {
                  const selected = isSameOption(item, value);
                  return (
                    <div
                      key={extractKey(item, index)}
                      id={optionId(index)}
                      role="option"
                      aria-selected={selected}
                      className={`cdd-option${selected ? " cdd-option-selected" : ""}${
                        optionId(index) === activeId ? " cdd-option-active" : ""
                      }`}
                      onMouseEnter={() => setActiveIndex(index)}
                      onClick={() => handleSelect(item)}
                    >
                      {extractLabel(item)}
                    </div>
                  );
                })
              ) : (
                <div className="cdd-noResults">No results found</div>
              )}
            </div>
          </div>,
          document.body
        )
      : null;

  const control = (
    <div className="cdd-row">
      <div className="cdd-searchWrap">
        <button
          type="button"
          id={triggerId}
          ref={triggerRef}
          className={`cui-control cdd-searchTrigger${open ? " cdd-searchTrigger-open cui-control-focused" : ""}${
            error ? " cui-control-error" : ""
          }`}
          disabled={disabled}
          onClick={() => (open ? closeMenu(false) : openMenu())}
          onKeyDown={onKeyDown}
          title={hasValue ? selectedLabel : ""}
          role="combobox"
          aria-label={fieldLabel ? undefined : emptyLabel}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={open ? listboxId : undefined}
          aria-activedescendant={search ? undefined : activeId}
          aria-invalid={Boolean(error)}
          aria-describedby={fieldIds.describedBy(error, hint)}
        >
          <span className={`cdd-searchTriggerLabel${hasValue ? "" : " cdd-placeholder"}`}>
            {hasValue ? selectedLabel : emptyLabel}
          </span>
          <svg
            viewBox="0 0 24 24"
            className={`cdd-chevron${open ? " cdd-chevron-open" : ""}`}
            aria-hidden="true"
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>

        {menu}
      </div>

      {clearable && hasValue && (
        <button
          type="button"
          title="Remove"
          aria-label={`Clear ${fieldLabel || "selection"}`}
          className="cdd-clearBtn"
          onClick={onClear}
          disabled={disabled}
        >
          <Trash2 size={16} />
        </button>
      )}

      {required && !fieldLabel && <span className="cdd-required">*</span>}
    </div>
  );

  return (
    <CustomFormField
      label={fieldLabel}
      isRequired={required}
      htmlFor={triggerId}
      hint={hint}
      error={error}
      colClass={colClass}
      noMargin={noMargin}
      className={`cdd-group${className ? ` ${className}` : ""}`}
      style={style}
    >
      {control}
    </CustomFormField>
  );
}

export default React.memo(CustomDropdowns);